import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MenuItem, Select, FormControl, Button, Popover } from '@mui/material';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { format } from 'date-fns';
import { ThemeContext } from '../Homepage';
import {
  updatedateselection,
  updatedateselection_sdate_edate,
  updateselecteddates_dashboard
} from '../../store/mainpage_data';

/**
 * WidgetDateSelector
 * A clone of Year component dedicated for widget extended properties.
 * Props:
 *  - initialPeriod (string) optional preset period (e.g., 'Last Month', 'Today', 'Custom')
 *  - initialCustomRange ({ startDate, endDate }) when initialPeriod === 'Custom'
 *  - disableInteraction (bool) to lock after initial selection
 */
const timePeriodOptionsBase = [
  'Today',
  'Yesterday',
  'This Week',
  'Last Week',
  'This Month',
  'Last Month',
  'Custom'
];

function WidgetDateSelector({ initialPeriod = null, initialCustomRange = null, disableInteraction = false }) {
  const dispatch = useDispatch();
  const { theme } = useContext(ThemeContext) || { theme: 'light' }; // fallback
  const [timePeriodOptions, setTimePeriodOptions] = useState(timePeriodOptionsBase);
  const [selectedValue, setSelectedValue] = useState('');
  const [customDateRange, setCustomDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: 'selection' }
  ]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isDateRangeConfirmed, setIsDateRangeConfirmed] = useState(false);
  const spanRef = React.useRef();

  const handleMenuItemClick = (event, period) => {
    if (disableInteraction) return;
    if (period === 'Custom') setAnchorEl(spanRef.current); else setAnchorEl(null);
    setSelectedValue(period);
  };

  const ensureOptionsTailCustom = (options) => {
    const copy = [...options];
    const last = copy[copy.length - 1];
    if (last !== 'Custom') {
      // ensure Custom present at end
      const filtered = copy.filter(o => o !== 'Custom');
      filtered.push('Custom');
      return filtered;
    }
    return copy;
  };
  const handleSelectChange = (event) => {
    if (disableInteraction) return;
    const value = event.target.value;
    setTimePeriodOptions(o => ensureOptionsTailCustom(o));
    setSelectedValue(value);
    setIsSelectOpen(false);
    applyPreset(value);
  };

  const convertEndDate = (startDate, endDate) => {
    const end = new Date(endDate);
    end.setDate(end.getDate());
    end.setHours(23, 59, 59, 999);
    return end;
  };

  const dispatchRange = (startForDash, endForDash, label) => {
    dispatch(updateselecteddates_dashboard({
      selectedstartdate_dashboard: startForDash,
      selectedenddate_dashboard: endForDash
    }));
    dispatch(updatedateselection(label));
  };

  const dispatchEpochRange = (epochStart, epochEnd, repStart, repEnd, label) => {
    dispatch(updatedateselection_sdate_edate({
      selectedstartdate: epochStart,
      selectedenddate: epochEnd,
      selectedrepstartdate: repStart,
      selectedrependdate: repEnd
    }));
    dispatch(updatedateselection(label));
  };

  const applyPreset = (preset) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDate = now.getDate();
    const dayOfWeek = now.getDay();
    let startDash, endDash, repStart, repEnd, epochStart, epochEnd;

    switch (preset) {
      case 'This Month':
        startDash = new Date(currentYear, currentMonth, 1);
        endDash = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59, 999);
        repStart = new Date(currentYear, currentMonth, 1, 0, 0, 0, 0).toString();
        repEnd = now.toString();
        break;
      case 'Last Month': {
        const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const yearForLast = currentMonth === 0 ? currentYear - 1 : currentYear;
        startDash = new Date(yearForLast, lastMonth, 1);
        endDash = new Date(yearForLast, lastMonth + 1, 0, 23, 59, 59, 999);
        repStart = new Date(yearForLast, lastMonth, 1, 0, 0, 0, 0).toString();
        repEnd = endDash.toString();
        break; }
      case 'This Week': {
        const startOfWeek = new Date(now); startOfWeek.setDate(currentDate - dayOfWeek); startOfWeek.setHours(0,0,0,0);
        const endOfWeek = new Date(startOfWeek); endOfWeek.setDate(startOfWeek.getDate() + 6); endOfWeek.setHours(23,59,59,999);
        startDash = startOfWeek; endDash = endOfWeek; repStart = startOfWeek.toString(); repEnd = now.toString();
        break; }
      case 'Last Week': {
        const startOfLastWeek = new Date(now); startOfLastWeek.setDate(currentDate - dayOfWeek - 7); startOfLastWeek.setHours(0,0,0,0);
        const endOfLastWeek = new Date(startOfLastWeek); endOfLastWeek.setDate(startOfLastWeek.getDate() + 6); endOfLastWeek.setHours(23,59,59,999);
        startDash = startOfLastWeek; endDash = endOfLastWeek; repStart = startDash.toString(); repEnd = endDash.toString();
        break; }
      case 'Today': {
        const start = new Date(now); start.setHours(0,0,0,0);
        const end = new Date(now); end.setHours(23,59,59,999);
        startDash = start; endDash = end;
        // epoch style
        const secondsZero = new Date(start);
        epochStart = Math.floor(secondsZero.getTime()/1000);
        epochEnd = epochStart + 23*3600 + 59*60 + 59;
        repStart = start.toString(); repEnd = end.toString();
        break; }
      case 'Yesterday': {
        const y = new Date(now); y.setDate(currentDate - 1);
        const start = new Date(y); start.setHours(0,0,0,0);
        const end = new Date(y); end.setHours(23,59,59,999);
        startDash = start; endDash = end;
        const secondsZero = new Date(start);
        epochStart = Math.floor(secondsZero.getTime()/1000);
        epochEnd = epochStart + 23*3600 + 59*60 + 59;
        repStart = start.toString(); repEnd = end.toString();
        break; }
      case 'Custom':
        // handled separately when user confirms range
        return;
      default:
        // Fallback to Today
        return applyPreset('Today');
    }

    // Dispatch updates (handle epoch if computed)
    if (epochStart != null && epochEnd != null) {
      dispatchEpochRange(epochStart, epochEnd, repStart, repEnd, preset);
    } else {
      dispatch(updatedateselection(preset));
    }
    dispatchRange(startDash, endDash, preset);
  };

  const handleDateChange = (item) => {
    if (disableInteraction) return;
    setCustomDateRange([item.selection]);
    const { startDate, endDate } = item.selection;
    if (startDate && endDate && startDate !== endDate) setIsDateRangeConfirmed(true); else setIsDateRangeConfirmed(false);
  };

  const handleCustomDateConfirm = () => {
    if (disableInteraction) return;
    const range = customDateRange[0];
    const start = range.startDate;
    const end = convertEndDate(range.startDate, range.endDate);
    const label = `${format(start,'MM/dd/yyyy')} - ${format(range.endDate,'MM/dd/yyyy')}`;
    setTimePeriodOptions(o => ensureOptionsTailCustom(o.concat(label)));
    setSelectedValue(label);
    setAnchorEl(null);
    dispatch(updatedateselection_sdate_edate({
      selectedstartdate: start,
      selectedenddate: end,
      selectedrepstartdate: start,
      selectedrependdate: end
    }));
    dispatch(updateselecteddates_dashboard({
      selectedstartdate_dashboard: start,
      selectedenddate_dashboard: end
    }));
    dispatch(updatedateselection('Custom'));
  };

  // Initial mount handling
  useEffect(() => {
    if (initialPeriod === 'Custom' && initialCustomRange?.startDate && initialCustomRange?.endDate) {
      setCustomDateRange([{ startDate: new Date(initialCustomRange.startDate), endDate: new Date(initialCustomRange.endDate), key: 'selection' }]);
      setSelectedValue('Custom');
      const start = new Date(initialCustomRange.startDate);
      const end = convertEndDate(initialCustomRange.startDate, initialCustomRange.endDate);
      dispatch(updatedateselection_sdate_edate({
        selectedstartdate: start,
        selectedenddate: end,
        selectedrepstartdate: start,
        selectedrependdate: end
      }));
      dispatch(updateselecteddates_dashboard({ selectedstartdate_dashboard: start, selectedenddate_dashboard: end }));
      dispatch(updatedateselection('Custom'));
    } else {
      applyPreset(initialPeriod || 'Today');
      setSelectedValue(initialPeriod || 'Today');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <FormControl>
        <Select
          value={selectedValue}
          onChange={handleSelectChange}
          onOpen={() => !disableInteraction && setIsSelectOpen(true)}
          open={isSelectOpen && !disableInteraction}
          onClose={() => setIsSelectOpen(false)}
          displayEmpty
          variant="standard"
          disableUnderline={disableInteraction}
          renderValue={(value) => {
            if (!value) {
              return (
                <span style={{ color: theme === 'light' ? '#C1C1C1' : '#8A8A8A' }}>
                  Select a time period
                </span>
              );
            }
            return value;
          }}
          IconComponent={(props) => (
            <ExpandMoreOutlinedIcon
              style={{
                width: '16px',
                height: '16px',
                color: disableInteraction
                  ? (theme === 'light' ? '#C1C1C1' : '#3C3C3C')
                  : (theme === 'light' ? '#3C3C3C' : '#FFFFFF')
              }}
            />
          )}
          sx={{
            border: 0,
            width: '222.35px',
            bgcolor: 'none',
            color: theme === 'light' ? '#3C3C3C' : '#FFFFFF',
            fontSize: '14px',
            fontFamily: 'Roboto',
            fontWeight: 400,
            textAlign: 'left',
            borderRadius: 0,
            marginRight: '16px',
            padding: "5px",
            '& .MuiSelect-select': {
              color: selectedValue === ''
                ? (theme === 'light' ? '#C1C1C1' : '#8A8A8A')
                : (theme === 'light' ? '#3C3C3C' : '#FFFFFF'),
              opacity: 1,
            },
            '& .Mui-disabled': {
              borderBottom: '1px solid',
              borderBottomColor: theme === 'light' ? '#C1C1C1' : '#3C3C3C',
              WebkitTextFillColor: theme === 'light' ? '#C1C1C1' : '#3C3C3C',
              color: theme === 'light' ? '#C1C1C1' : '#3C3C3C',
            },
            '& .MuiSvgIcon-root': {
              fill: disableInteraction
                ? theme === 'light' ? '#C1C1C1' : '#3C3C3C'
                : theme === 'light' ? '#3C3C3C' : '#FFFFFF',
            },
            ':before': { borderBottom: `1px solid ${theme === 'light' ? '#C7CED3' : '#FFFFFF'}` },
            ':after': { borderBottom: '2px solid #008CD7' },
            '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 0,
            },
            '& .MuiInputBase-input:focus': {
              color: '#008CD7',
              bgcolor: 'none',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 0,
            },
            '& .MuiInputLabel-root': { display: 'none' },
            '& .MuiSelect-select': {
              paddingTop: "0px",
              paddingBottom: "0px",
              marginTop: "0px",
            },
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: theme === 'light' ? '#C1C1C1' : '#3C3C3C',
            },
          }}
          ref={spanRef}
          disabled={disableInteraction}
          MenuProps={{
            disablePortal: true,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            PaperProps: {
              sx: {
                bgcolor: theme === 'dark' ? '#3C3C3C' : '#C1C1C1',
                borderBottom: '2px solid #008CD7',
                borderRadius: 0,
                width: '222.35px',
                maxWidth: '100%',
                boxShadow: 'none',
                maxHeight: '250px',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '4px',
                  height: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  borderRadius: '0px',
                  background: theme === 'light' ? '#ffffff' : '#1e1e1e',
                  margin: '4px 0px 4px 0px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#008CD7',
                  borderRadius: '5px',
                },
                '& .MuiMenuItem-root.Mui-selected': {
                  fontWeight: 700,
                  color: '#000000'
                },
                '& .MuiMenuItem-root:hover': {
                  backgroundColor: '#005C8E',
                  color: '#000000'
                },
                color: theme === 'dark' ? '#FFFFFF' : '#3C3C3C',
                '& .MuiList-root.MuiMenu-list': {
                  padding: '4px 0px 4px 0px'
                },
              }
            },
          }}
        >
          {/* <MenuItem
            value=""
            disabled
            sx={{
              fontSize: '14px',
              fontFamily: 'Roboto',
              fontWeight: 400,
              color:"orange",
              // Force disabled placeholder color to appear regardless of MUI default opacity
              '&.Mui-disabled': {
                // color: theme === 'dark' ? '#FFFFFF' : '#3C3C3C',
                opacity: 1,
              },
            }}
          >
            Select a time period
          </MenuItem> */}
          {timePeriodOptions.map(period => (
            <MenuItem
              key={period}
              value={period}
              onClick={(e) => handleMenuItemClick(e, period)}
              sx={{ fontSize: '14px', fontFamily: 'Roboto', fontWeight: 400 }}
            >
              {period}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Popover
        open={Boolean(anchorEl) && !disableInteraction}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <div style={{ padding: '20px' }}>
          {/* Lazy load range picker to avoid extra bundle weight? Keeping inline for now */}
          <input
            type="date"
            value={format(customDateRange[0].startDate, 'yyyy-MM-dd')}
            onChange={e => handleDateChange({ selection: { ...customDateRange[0], startDate: new Date(e.target.value), endDate: customDateRange[0].endDate } })}
            style={{ marginRight: 8 }}
          />
          <input
            type="date"
            value={format(customDateRange[0].endDate, 'yyyy-MM-dd')}
            onChange={e => handleDateChange({ selection: { ...customDateRange[0], endDate: new Date(e.target.value) } })}
            style={{ marginRight: 8 }}
          />
          <Button onClick={handleCustomDateConfirm} color="primary" disabled={!isDateRangeConfirmed}>Confirm</Button>
        </div>
      </Popover>
    </div>
  );
}

export default WidgetDateSelector;